import { configure } from "mobx";
import { createContext, useContext } from "react";
import { RootStore } from "./root.store";
import { observer } from "mobx-react";
import { installMobxWarnTracker } from "@/lib/mobx-warn-tracker";

installMobxWarnTracker();

configure({
  enforceActions: "always",
  ...(import.meta.env.DEV
    ? {
        computedRequiresReaction: true,
        // `reactionRequiresObservable` is intentionally disabled: the
        // `mobx/missing-observer` ESLint rule wraps every component in
        // `observer`, including ones that do not read observables. That
        // produces unavoidable "created without reading any observable"
        // warnings in dev if this flag is on.
        reactionRequiresObservable: false,
        observableRequiresReaction: true,
        disableErrorBoundaries: false,
      }
    : {}),
});

export const StoreContext = createContext<RootStore | null>(null);

let rootStoreInstance: RootStore | null = null;

function initializeStore(): RootStore {
  if (!rootStoreInstance) {
    rootStoreInstance = new RootStore();
  }
  return rootStoreInstance;
}

/** Access the singleton RootStore from outside React (e.g. route loaders).
 *  Safe to call before any component mounts — lazily constructs the instance
 *  so the StoreProvider later receives the same one. */
export function getRootStore(): RootStore {
  return initializeStore();
}

/** Replace the module-singleton with a caller-provided store. Used by the
 *  test renderer so route loaders see the same store the component reads
 *  through StoreContext. Production code must not call this. */
export function setRootStoreForTesting(store: RootStore | null): void {
  rootStoreInstance = store;
}

export const StoreProvider = observer(function StoreProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const store = initializeStore();
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
});

export function useStore(): RootStore {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error("useStore must be used within StoreProvider");
  }
  return store;
}
